import { useState, useEffect } from "react";
import heroImg from "../../../Assets/home/heroImg.png";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

export function FloatingTag({ color, label, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: [0, -6, 0] }}
      transition={{
        delay,
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut",
      }}
      className="px-6 py-3 rounded-full backdrop-blur-lg bg-black/50 border border-white/20 shadow-xl flex items-center gap-3"
    >
      <span className={`w-2.5 h-2.5 rounded-full ${color}`} />
      <span className="text-sm font-medium text-white">
        {label}
      </span>
    </motion.div>
  );
}

const HEADLINES = [
  {
    lang: "en",
    titleLine: "Stuck on the Highway?",
    accentLines: ["Help Is Already On Its Way."],
  },
  {
    lang: "hi",
    titleLine: "हाईवे पर फंस गए?",
    accentLines: ["दुर्घटना से समाधान तक,", "मदद रास्ते में।"],
  },
];

const HEADLINE_SWITCH_INTERVAL_MS = 3500;

export default function Hero() {
  const navigate = useNavigate();
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const activeHeadline = HEADLINES[headlineIndex];
  const isHindi = activeHeadline.lang === "hi";

  useEffect(() => {
    const interval = setInterval(() => {
      setHeadlineIndex((prev) => (prev + 1) % HEADLINES.length);
    }, HEADLINE_SWITCH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen text-white overflow-hidden">
          {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={heroImg}
          alt="Crane towing truck on highway"
          className="w-full h-full object-cover brightness-[0.65] contrast-[1.1]"
        />

        {/* Cinematic Gradient Overlay */}
        <div className="absolute inset-0">

          {/* Left readable area */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#020617]/85 via-[#020617]/60 via-[#020617]/20 to-transparent" />

            {/* Bottom contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/80 via-transparent to-transparent" />
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-28 grid lg:grid-cols-2 gap-12 items-center">
                {/* LEFT CONTENT */}
        <div>
          {/* Fixed min-height reserves space at the largest breakpoint so
              the layout doesn't jump when the language swaps */}
          <div className="min-h-[110px] md:min-h-[220px]">
            <AnimatePresence mode="wait">
              <motion.h1
                key={activeHeadline.lang}
                lang={activeHeadline.lang}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className={`text-3xl md:text-6xl font-semibold text-white ${
                  isHindi
                    ? "leading-[1.45] tracking-normal"
                    : "leading-[1.05] tracking-[-0.02em]"
                }`}
                style={{
                  textShadow: `
                    0 0 1px rgba(255,255,255,0.15),
                    0 0 12px rgba(0,0,0,0.6),
                    0 0 40px rgba(0,0,0,0.9)
                  `
                }}
              >
                {activeHeadline.titleLine}
                <span
                  className={`block text-[#21A6E9] ${isHindi ? "mt-3" : "mt-2"}`}
                  style={{
                    textShadow: `
                      0 0 1px rgba(33,166,233,0.4),
                      0 0 20px rgba(33,166,233,0.35),
                      0 0 60px rgba(0,0,0,0.9)
                    `
                  }}
                >
                  {activeHeadline.accentLines.map((line, i) => (
                    <span key={i} className="block">
                      {line}
                    </span>
                  ))}
                </span>
              </motion.h1>
            </AnimatePresence>
          </div>

          <p className="mt-6 text-slate-200 text-lg max-w-xl leading-relaxed
                        drop-shadow-[0_2px_15px_rgba(0,0,0,0.7)]">
                  Accident, breakdown or vehicle seizure — ClaimPro dispatches cranes,
                  recovery vans and legal teams in real-time so you’re never left
                  waiting.
          </p>

          <div className="mt-8 flex gap-4">
            <button
              onClick={() => navigate("/get-help")}
              className="relative overflow-hidden bg-[#21A6E9] px-7 py-3 rounded-xl font-medium
                            shadow-lg shadow-[#21A6E9]/30 hover:shadow-[#21A6E9]/50 transition duration-300"
            >
              Request Help Now
            </button>

            <button 
              onClick={() => navigate("/loginPage")}
              className="border border-white/40 hover:border-white px-6 py-3 rounded-xl">
                  Track Existing Case
            </button>
                </div>
          </div>

              {/* Floating Service Indicators */}
          <div className="hidden lg:block absolute right-[8%] top-[35%] space-y-4">
              <FloatingTag color="bg-red-400" label="Accident Support" delay={0} />
              <FloatingTag color="bg-yellow-400" label="Crane Dispatched" delay={0.8} />
              <FloatingTag color="bg-blue-400" label="Breakdown Repair" delay={1.6} />
              <FloatingTag color="bg-purple-400" label="Legal Team Notified" delay={2.4} />
          </div>
      </div>
    </section>
  );
}